import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import Breadcrumbs from '../components/Breadcrumbs';
import { Candidate } from '../types';
import { mockRecruitmentData } from '../utils/mockRecruitmentData';

const Container = styled.div`
  padding: ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  background-color: ${Colors.background};
`;

const ProfileLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: ${Spacing.xl}px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

const ProfileCard = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
  text-align: center;
`;

const ProfileAvatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${BorderRadius.round}px;
  background: linear-gradient(135deg, ${Colors.primary}, ${Colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.hero}px;
  font-weight: ${FontWeights.bold};
  margin: 0 auto ${Spacing.lg}px;
`;

const CandidateName = styled.h1`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.sm}px 0;
`;

const CandidateTitle = styled.p`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
  margin: 0 0 ${Spacing.lg}px 0;
`;

const ContactInfo = styled.div`
  text-align: left;
  padding-top: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.sm}px 0;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const QuickStats = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.md}px 0;
  border-bottom: 1px solid ${Colors.borderLight};
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatLabel = styled.span`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

const StatValue = styled.span`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xl}px;
`;

const TabContainer = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const TabNavigation = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.borderLight};
`;

const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  background-color: ${props => props.active ? Colors.surface : 'transparent'};
  color: ${props => props.active ? Colors.primary : Colors.textSecondary};
  border: none;
  border-bottom: 2px solid ${props => props.active ? Colors.primary : 'transparent'};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
  }
`;

const TabContent = styled.div`
  padding: ${Spacing.xl}px;
`;

const Section = styled.div`
  margin-bottom: ${Spacing.xl}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
  margin: 0 0 ${Spacing.md}px 0;
`;

const SkillsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm}px;
`;

const SkillBadge = styled.span`
  padding: ${Spacing.xs}px ${Spacing.md}px;
  background-color: ${Colors.backgroundDark};
  color: ${Colors.textPrimary};
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.sm}px;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: ${Spacing.xl}px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: ${Spacing.lg}px;
  
  &::before {
    content: '';
    position: absolute;
    left: -${Spacing.xl}px;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: ${BorderRadius.round}px;
    background-color: ${Colors.primary};
  }
  
  &::after {
    content: '';
    position: absolute;
    left: -${Spacing.xl - 3}px;
    top: 16px;
    bottom: 0;
    width: 2px;
    background-color: ${Colors.borderLight};
  }
  
  &:last-child::after {
    display: none;
  }
`;

const TimelineDate = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  margin-bottom: ${Spacing.xs}px;
`;

const TimelineContent = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const AssessmentCard = styled.div`
  background-color: ${Colors.backgroundDark};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
  margin-bottom: ${Spacing.md}px;
`;

const ScoreDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const ScoreLabel = styled.div`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textPrimary};
`;

const ScoreValue = styled.div<{ score: number }>`
  font-size: ${FontSizes.xxl}px;
  font-weight: ${FontWeights.bold};
  color: ${props => 
    props.score >= 80 ? Colors.success :
    props.score >= 60 ? Colors.warning :
    Colors.danger
  };
`;

const ScoreBar = styled.div`
  height: 8px;
  background-color: ${Colors.border};
  border-radius: 4px;
  overflow: hidden;
`;

const ScoreFill = styled.div<{ score: number }>`
  height: 100%;
  width: ${props => props.score}%;
  background-color: ${props => 
    props.score >= 80 ? Colors.success :
    props.score >= 60 ? Colors.warning :
    Colors.danger
  };
  transition: width 300ms ease;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: ${Spacing.md}px;
  background-color: ${props => {
    switch (props.variant) {
      case 'primary': return Colors.primary;
      case 'secondary': return Colors.secondary;
      case 'danger': return Colors.danger;
      default: return Colors.primary;
    }
  }};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: ${Shadows.md};
  }
`;

const CandidateProfileScreen: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'assessments' | 'offer'>('overview');
  
  // Find the candidate by ID
  const candidate = mockRecruitmentData.candidates.find(c => c.id === id) || mockRecruitmentData.candidates[0];
  
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const breadcrumbItems = [
    { label: 'Candidates', path: '/candidates' },
    { label: candidate.name, path: `/candidates/${candidate.id}` },
  ];

  return (
    <Container>
      <Breadcrumbs items={breadcrumbItems} />
      
      <ProfileLayout>
        <SidePanel>
          <ProfileCard>
            <ProfileAvatar>{getInitials(candidate.name)}</ProfileAvatar>
            <CandidateName>{candidate.name}</CandidateName>
            <CandidateTitle>{candidate.currentRole || 'Candidate'}</CandidateTitle>
            
            <ContactInfo>
              <ContactItem>
                <span>📧</span>
                <span>{candidate.email}</span>
              </ContactItem>
              {candidate.phone && (
                <ContactItem>
                  <span>📱</span>
                  <span>{candidate.phone}</span>
                </ContactItem>
              )}
              <ContactItem>
                <span>📍</span>
                <span>{candidate.location}</span>
              </ContactItem>
              <ContactItem>
                <span>🔗</span>
                <span>{candidate.source}</span>
              </ContactItem>
            </ContactInfo>
          </ProfileCard>

          <QuickStats>
            <StatItem>
              <StatLabel>Requisition</StatLabel>
              <StatValue>{candidate.requisitionTitle}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Current Stage</StatLabel>
              <StatValue>{candidate.stage}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Days in Stage</StatLabel>
              <StatValue>{candidate.stageAge}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Experience</StatLabel>
              <StatValue>{candidate.yearsOfExperience || 'N/A'} years</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Applied Date</StatLabel>
              <StatValue>{new Date(candidate.appliedDate).toLocaleDateString()}</StatValue>
            </StatItem>
          </QuickStats>
        </SidePanel>

        <MainContent>
          <TabContainer>
            <TabNavigation>
              <TabButton 
                active={activeTab === 'overview'} 
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </TabButton>
              <TabButton 
                active={activeTab === 'timeline'} 
                onClick={() => setActiveTab('timeline')}
              >
                Timeline
              </TabButton>
              <TabButton 
                active={activeTab === 'assessments'} 
                onClick={() => setActiveTab('assessments')}
              >
                Assessments
              </TabButton>
              <TabButton 
                active={activeTab === 'offer'} 
                onClick={() => setActiveTab('offer')}
              >
                Offer
              </TabButton>
            </TabNavigation>

            <TabContent>
              {activeTab === 'overview' && (
                <>
                  <Section>
                    <SectionTitle>Skills & Expertise</SectionTitle>
                    <SkillsGrid>
                      {candidate.skills.map(skill => (
                        <SkillBadge key={skill}>{skill}</SkillBadge>
                      ))}
                    </SkillsGrid>
                  </Section>

                  <Section>
                    <SectionTitle>Summary</SectionTitle>
                    <p style={{ color: Colors.textPrimary, lineHeight: 1.6 }}>
                      {candidate.notes || `${candidate.name} is a ${candidate.currentRole || 'professional'} with ${candidate.yearsOfExperience || 'several'} years of experience. Currently applying for the ${candidate.requisitionTitle} position.`}
                    </p>
                  </Section>

                  <Section>
                    <SectionTitle>Documents</SectionTitle>
                    <div style={{ display: 'flex', gap: `${Spacing.md}px` }}>
                      <ActionButton variant="secondary">
                        📄 View Resume
                      </ActionButton>
                      <ActionButton variant="secondary">
                        📁 View Portfolio
                      </ActionButton>
                    </div>
                  </Section>
                </>
              )}

              {activeTab === 'timeline' && (
                <Section>
                  <SectionTitle>Activity Timeline</SectionTitle>
                  <Timeline>
                    <TimelineItem>
                      <TimelineDate>{new Date(candidate.lastActivity).toLocaleDateString()}</TimelineDate>
                      <TimelineContent>Last activity - Stage: {candidate.stage}</TimelineContent>
                    </TimelineItem>
                    {candidate.interviewScore && (
                      <TimelineItem>
                        <TimelineDate>{new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toLocaleDateString()}</TimelineDate>
                        <TimelineContent>Completed interview - Score: {candidate.interviewScore}%</TimelineContent>
                      </TimelineItem>
                    )}
                    {candidate.assessmentScore && (
                      <TimelineItem>
                        <TimelineDate>{new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toLocaleDateString()}</TimelineDate>
                        <TimelineContent>Completed assessment - Score: {candidate.assessmentScore}%</TimelineContent>
                      </TimelineItem>
                    )}
                    <TimelineItem>
                      <TimelineDate>{new Date(candidate.appliedDate).toLocaleDateString()}</TimelineDate>
                      <TimelineContent>Applied for position via {candidate.source}</TimelineContent>
                    </TimelineItem>
                  </Timeline>
                </Section>
              )}

              {activeTab === 'assessments' && (
                <Section>
                  <SectionTitle>Assessment Results</SectionTitle>
                  {candidate.assessmentScore && (
                    <AssessmentCard>
                      <ScoreDisplay>
                        <ScoreLabel>Technical Assessment</ScoreLabel>
                        <ScoreValue score={candidate.assessmentScore}>
                          {candidate.assessmentScore}%
                        </ScoreValue>
                      </ScoreDisplay>
                      <ScoreBar>
                        <ScoreFill score={candidate.assessmentScore} />
                      </ScoreBar>
                    </AssessmentCard>
                  )}
                  {candidate.interviewScore && (
                    <AssessmentCard>
                      <ScoreDisplay>
                        <ScoreLabel>Interview Score</ScoreLabel>
                        <ScoreValue score={candidate.interviewScore}>
                          {candidate.interviewScore}%
                        </ScoreValue>
                      </ScoreDisplay>
                      <ScoreBar>
                        <ScoreFill score={candidate.interviewScore} />
                      </ScoreBar>
                    </AssessmentCard>
                  )}
                </Section>
              )}

              {activeTab === 'offer' && (
                <Section>
                  <SectionTitle>Offer Details</SectionTitle>
                  {candidate.offerDetails ? (
                    <>
                      <StatItem>
                        <StatLabel>Base Salary</StatLabel>
                        <StatValue>€{candidate.offerDetails.salary.toLocaleString()}</StatValue>
                      </StatItem>
                      {candidate.offerDetails.bonus && (
                        <StatItem>
                          <StatLabel>Bonus</StatLabel>
                          <StatValue>€{candidate.offerDetails.bonus.toLocaleString()}</StatValue>
                        </StatItem>
                      )}
                      {candidate.offerDetails.startDate && (
                        <StatItem>
                          <StatLabel>Start Date</StatLabel>
                          <StatValue>{new Date(candidate.offerDetails.startDate).toLocaleDateString()}</StatValue>
                        </StatItem>
                      )}
                      {candidate.offerDetails.expiryDate && (
                        <StatItem>
                          <StatLabel>Offer Expires</StatLabel>
                          <StatValue>{new Date(candidate.offerDetails.expiryDate).toLocaleDateString()}</StatValue>
                        </StatItem>
                      )}
                    </>
                  ) : (
                    <p style={{ color: Colors.textSecondary }}>No offer has been extended yet.</p>
                  )}
                </Section>
              )}
            </TabContent>

            <ActionButtons>
              <ActionButton variant="primary">
                Advance Stage
              </ActionButton>
              <ActionButton variant="secondary">
                Schedule Interview
              </ActionButton>
              <ActionButton variant="secondary">
                Send Email
              </ActionButton>
              <ActionButton variant="danger">
                Reject
              </ActionButton>
            </ActionButtons>
          </TabContainer>
        </MainContent>
      </ProfileLayout>
    </Container>
  );
};

export default CandidateProfileScreen;